import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Loader2, Brain, Sparkles, X, BarChart3 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";

interface Props {
  childId: string;
  childName: string;
  nurseryId: string;
}

const BehaviorAnalysisPanel = ({ childId, childName, nurseryId }: Props) => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{
    analysis: string;
    total_observations: number;
    positive_count: number;
    negative_count: number;
    top_tags: [string, number][];
  } | null>(null);
  const [showPanel, setShowPanel] = useState(false);

  const runAnalysis = async () => {
    setLoading(true);
    setShowPanel(true);
    try {
      const { data, error } = await supabase.functions.invoke("analyze-child-behavior", {
        body: { child_id: childId, nursery_id: nurseryId },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      setResult(data);
      toast.success("تم إنشاء التحليل بنجاح");
    } catch (e: any) {
      toast.error(e.message || "فشل في تحليل السلوك");
      setShowPanel(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        onClick={runAnalysis}
        disabled={loading}
        className="gap-2"
        variant="outline"
      >
        {loading ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Brain className="w-4 h-4" />
        )}
        تحليل سلوك الطفل بالذكاء الاصطناعي
      </Button>

      <AnimatePresence>
        {showPanel && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
            onClick={(e) => {
              if (e.target === e.currentTarget && !loading) setShowPanel(false);
            }}
          >
            <Card className="w-full max-w-3xl max-h-[85vh] flex flex-col shadow-xl">
              <CardHeader className="pb-3 flex-shrink-0">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-primary" />
                    تحليل سلوك {childName}
                  </CardTitle>
                  {!loading && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => setShowPanel(false)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="flex-1 overflow-hidden">
                {loading ? (
                  <div className="flex flex-col items-center justify-center py-16 gap-4">
                    <div className="relative">
                      <Brain className="w-12 h-12 text-primary animate-pulse" />
                      <Sparkles className="w-5 h-5 text-amber-500 absolute -top-1 -right-1 animate-bounce" />
                    </div>
                    <p className="text-muted-foreground text-sm">
                      جاري تحليل ملاحظات {childName} وتوليد الخطة العلاجية...
                    </p>
                    <Loader2 className="w-5 h-5 animate-spin text-primary" />
                  </div>
                ) : result ? (
                  <ScrollArea className="h-[calc(85vh-120px)]">
                    <div className="space-y-4 pb-4">
                      {/* Stats summary */}
                      <div className="grid grid-cols-3 gap-3">
                        <div className="rounded-lg border bg-muted/30 p-3 text-center">
                          <p className="text-2xl font-bold text-foreground">
                            {result.total_observations}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            إجمالي الملاحظات
                          </p>
                        </div>
                        <div className="rounded-lg border bg-emerald-50 dark:bg-emerald-950/30 p-3 text-center">
                          <p className="text-2xl font-bold text-emerald-600">
                            {result.positive_count}
                          </p>
                          <p className="text-xs text-muted-foreground">إيجابية</p>
                        </div>
                        <div className="rounded-lg border bg-red-50 dark:bg-red-950/30 p-3 text-center">
                          <p className="text-2xl font-bold text-red-600">
                            {result.negative_count}
                          </p>
                          <p className="text-xs text-muted-foreground">سلبية</p>
                        </div>
                      </div>

                      {/* Top tags */}
                      {result.top_tags && result.top_tags.length > 0 && (
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <BarChart3 className="w-4 h-4 text-primary" />
                            <span className="text-sm font-medium">
                              أكثر الوسوم تكراراً
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-1.5">
                            {result.top_tags.map(([tag, count]) => (
                              <Badge
                                key={tag}
                                variant="secondary"
                                className="text-xs"
                              >
                                {tag} ({count})
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* AI Analysis */}
                      <div className="prose prose-sm dark:prose-invert max-w-none rounded-lg border bg-muted/20 p-4" dir="rtl">
                        <ReactMarkdown>{result.analysis}</ReactMarkdown>
                      </div>
                    </div>
                  </ScrollArea>
                ) : null}
              </CardContent>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default BehaviorAnalysisPanel;
